import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { Avatar, Pill, Spinner, strengthColor, companyStatusColor, stageColor } from "../components/ui-bits";
import { ActionMenu, ConfirmDialog } from "../components/crud";
import { CompanyForm } from "../components/record-forms";
import { useToast } from "../hooks/use-toast";
import { ArrowLeft, Globe, MapPin, Users, Building2, Briefcase, FileText } from "lucide-react";

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    api.company(id).then(setCompany).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Spinner />;
  if (!company) return <div className="p-6 text-[13px]">Company not found.</div>;

  const save = async (values) => {
    const updated = await api.updateCompany(id, values);
    setCompany((current) => ({ ...current, ...updated }));
    setShowEdit(false);
    toast({ title: "Company updated", description: updated.name });
  };
  const remove = async () => {
    setDeleteBusy(true);
    try {
      await api.deleteCompany(id);
      toast({ title: "Company deleted", description: company.name });
      navigate("/companies");
    } catch (error) {
      toast({ title: "Could not delete company", description: error?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-[1100px] mx-auto animate-in-up">
      <button onClick={() => navigate("/companies")} className="inline-flex items-center gap-1 text-[13px] text-[hsl(240,4%,50%)] hover:text-[hsl(240,6%,10%)] mb-3"><ArrowLeft className="h-4 w-4" /> Companies</button>

      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-xl bg-[hsl(240,5%,92%)] text-[hsl(240,5%,35%)] flex items-center justify-center text-[22px] font-semibold shrink-0">{company.logo}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-[22px] font-semibold">{company.name}</h1>
            <Pill className={companyStatusColor[company.status] || companyStatusColor.Lead}>{company.status}</Pill>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-[13px] text-[hsl(240,4%,48%)]">
            <span className="inline-flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" />{company.domain}</span>
            <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />{company.industry}</span>
            <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{company.employees}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{company.location}</span>
            <span className="font-medium text-[hsl(240,6%,20%)]">{company.arr} ARR</span>
          </div>
        </div>
        <ActionMenu onEdit={() => setShowEdit(true)} onDelete={() => setShowDelete(true)} />
      </div>

      <section className="mt-6 rounded-xl border border-[hsl(240,6%,91%)] bg-white">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[hsl(240,6%,92%)]">
          <FileText className="h-4 w-4 text-[hsl(240,4%,45%)]" />
          <h2 className="text-[14px] font-semibold">Description</h2>
        </div>
        <div className="px-4 py-4 text-[13px] leading-relaxed text-[hsl(240,5%,30%)] whitespace-pre-wrap break-words">
          {company.description || <span className="text-[hsl(240,4%,55%)]">No description added yet.</span>}
        </div>
      </section>

      {/* Associated people */}
      <div className="mt-6 rounded-xl border border-[hsl(240,6%,91%)] bg-white">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[hsl(240,6%,92%)]">
          <Users className="h-4 w-4 text-[hsl(240,4%,45%)]" />
          <h2 className="text-[14px] font-semibold">People at {company.name}</h2>
          <span className="text-[12px] text-[hsl(240,4%,55%)]">({(company.people || []).length})</span>
        </div>
        <div className="divide-y divide-[hsl(240,6%,94%)]">
          {(company.people || []).length === 0 && <div className="px-4 py-8 text-[13px] text-[hsl(240,4%,55%)]">No people linked to this company yet.</div>}
          {(company.people || []).map((p) => (
            <button key={p.id} onClick={() => navigate(`/people/${p.id}`)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[hsl(240,20%,99%)] text-left">
              <Avatar text={p.avatar} brand size={30} />
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-medium">{p.name}</div>
                <div className="text-[12px] text-[hsl(240,4%,50%)]">{p.job_title} · {p.email}</div>
              </div>
              <Pill className={strengthColor[p.connection_strength] || strengthColor.Weak}>{p.connection_strength}</Pill>
            </button>
          ))}
        </div>
      </div>

      {/* Deals */}
      <div className="mt-4 rounded-xl border border-[hsl(240,6%,91%)] bg-white">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[hsl(240,6%,92%)]">
          <Building2 className="h-4 w-4 text-[hsl(240,4%,45%)]" />
          <h2 className="text-[14px] font-semibold">Deals</h2>
          <span className="text-[12px] text-[hsl(240,4%,55%)]">({(company.deals || []).length})</span>
        </div>
        <div className="divide-y divide-[hsl(240,6%,94%)]">
          {(company.deals || []).length === 0 && <div className="px-4 py-8 text-[13px] text-[hsl(240,4%,55%)]">No deals yet.</div>}
          {(company.deals || []).map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[hsl(240,20%,99%)]">
              <div className="min-w-0 flex-1"><div className="text-[13.5px] font-medium">{d.name}</div><div className="text-[12px] text-[hsl(240,4%,50%)]">Close {d.close_date}</div></div>
              <Pill className={stageColor[d.stage] || stageColor.Discovery}>{d.stage}</Pill>
              <span className="text-[13px] font-medium tabular w-24 text-right">{d.value}</span>
            </div>
          ))}
        </div>
      </div>

      <CompanyForm open={showEdit} onClose={() => setShowEdit(false)} onSubmit={save} initialValues={company} />
      <ConfirmDialog open={showDelete} onClose={() => !deleteBusy && setShowDelete(false)} onConfirm={remove} busy={deleteBusy} title="Delete company?" description={`“${company.name}” will be permanently removed.`} />
    </div>
  );
}
